<template>
  <base-drawer :show="state.isOpen" title="Saved views" @close="close">
    <div v-if="loading" class="sv-status">
      <i class="el-icon-loading"></i>
      <span>Loading your views...</span>
    </div>

    <div v-else-if="loadError" class="sv-status">
      <span class="text-white">Could not load your saved views. Try again.</span>
    </div>

    <div v-else-if="sections.length === 0" class="sv-empty">
      <i class="tim-icons icon-book-bookmark sv-empty__icon"></i>
      <p class="sv-empty__title text-white">No saved views yet</p>
      <p class="sv-empty__hint">
        Filter a list the way you like, then open the views menu on that page's filter bar and save it. Your saved views show up here.
      </p>
    </div>

    <template v-else>
      <drawer-section
        v-for="s in sections"
        :key="s.key"
        :title="s.entry.label"
        :icon="s.entry.icon"
        collapsible
        :expanded="!!expanded[s.key]"
        @update:expanded="(v) => onExpand(s.key, v)"
      >
        <template v-if="s.entry.iconSvg" #icon>
          <span class="sv-section-icon mr-2" v-html="s.entry.iconSvg"></span>
        </template>
        <div v-for="p in s.presets" :key="p.id" class="sv-row">
          <div v-if="editingId === p.id" class="sv-row__edit">
            <input
              ref="editInput"
              v-model="editName"
              type="text"
              class="sv-input"
              maxlength="100"
              @keyup.enter="commitRename(p)"
              @keyup.esc.stop="cancelRename"
            />
            <button type="button" class="sv-icon-btn sv-icon-btn--confirm" title="Save" @click="commitRename(p)">
              <i class="tim-icons icon-check-2"></i>
            </button>
            <button type="button" class="sv-icon-btn" title="Cancel" @click="cancelRename">
              <i class="tim-icons icon-simple-remove"></i>
            </button>
          </div>

          <template v-else>
            <div class="sv-row__main" @click="openView(s.entry, p)">
              <span class="sv-row__name text-white">{{ p.name }}</span>
              <span class="sv-row__meta">{{ rowMeta(p.filters) }}</span>
            </div>
            <div class="sv-row__actions">
              <button
                type="button"
                class="sv-icon-btn"
                :class="{ 'sv-icon-btn--star-on': p.is_default }"
                :title="p.is_default ? 'Default view' : 'Set as default'"
                @click.stop="toggleDefault(p)"
              >
                <i class="tim-icons icon-shape-star"></i>
              </button>
              <button type="button" class="sv-icon-btn" title="Rename" @click.stop="startRename(p)">
                <i class="tim-icons icon-pencil"></i>
              </button>
              <button type="button" class="sv-icon-btn sv-icon-btn--danger" title="Delete" @click.stop="onDelete(p)">
                <i class="tim-icons icon-trash-simple"></i>
              </button>
            </div>
          </template>
        </div>
      </drawer-section>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance, set } from 'vue'
import swal from 'sweetalert2'
import { useSavedViewsDrawer, closeSavedViews } from '@/composables/useSavedViewsDrawer'
import {
  fetchSavedFilters,
  renamePreset,
  deletePreset,
  setPresetDefault,
  type SavedFilterRow,
} from '@/util/savedFiltersApi'
import {
  SAVED_VIEW_REGISTRY,
  getSavedViewEntry,
  getSavedViewEntity,
  buildSavedViewRoute,
  matchSavedViewKey,
  type SavedViewRegistryEntry,
} from '@/util/savedViewsRegistry'

const state = useSavedViewsDrawer()
const inst = getCurrentInstance()
const vm = inst ? (inst.proxy as any) : null

const rows = ref<SavedFilterRow[]>([])
const loading = ref(false)
const loadError = ref(false)
const expanded = reactive<Record<string, boolean>>({})

const editingId = ref<number | null>(null)
const editName = ref('')
const editInput = ref<HTMLInputElement | HTMLInputElement[] | null>(null)

// The view_key of the page the user is currently on (route path + query, so a
// per-entity board like /projects/12 and a tab like ?tab=2 both resolve).
const currentViewKey = computed<string | null>(() => {
  const path = vm?.$route?.path
  if (!path) return null
  return matchSavedViewKey(path, vm?.$route?.query || {})
})

// Named presets only (the name === '' last-used row is invisible plumbing).
const presetsByKey = computed<Record<string, SavedFilterRow[]>>(() => {
  const map: Record<string, SavedFilterRow[]> = {}
  for (const r of rows.value) {
    if (r.name === '') continue
    if (!map[r.view_key]) map[r.view_key] = []
    map[r.view_key].push(r)
  }
  return map
})

// Sections in display order: current page first, then registry order. Only
// registered view_keys with presets are shown; every page that saves views must
// have a registry entry, or its views are invisible here.
const sections = computed(() => {
  const byKey = presetsByKey.value
  const cur = currentViewKey.value
  const ordered: string[] = []
  if (cur && byKey[cur] && getSavedViewEntry(cur)) ordered.push(cur)
  for (const e of SAVED_VIEW_REGISTRY) {
    if (e.key !== cur && byKey[e.key]) ordered.push(e.key)
  }
  return ordered.map((k) => ({
    key: k,
    entry: getSavedViewEntry(k)!,
    presets: byKey[k],
  }))
})

async function load(): Promise<void> {
  loading.value = true
  loadError.value = false
  try {
    rows.value = await fetchSavedFilters()
    // Current page's section expanded, the rest collapsed.
    const cur = currentViewKey.value
    for (const k of Object.keys(presetsByKey.value)) set(expanded, k, k === cur)
  } catch (e) {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

function onExpand(key: string, val: boolean) {
  set(expanded, key, val)
}

// Compact, generic preview of a stored filter blob (values are page-owned, so
// count active fields rather than trying to render each shape).
function isActiveVal(v: any): boolean {
  if (v == null || v === '') return false
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object') return Object.values(v).some((x) => x !== '' && x != null && x !== false)
  return true
}
function filterSummary(filters: Record<string, any>): string {
  let c = 0
  for (const k in filters) {
    if (k.startsWith('_')) continue
    if (isActiveVal(filters[k])) c++
  }
  if (c === 0) return 'No filters'
  return c === 1 ? '1 filter' : `${c} filters`
}
// Per-entity views lead with the entity name (which project or survey) so two
// views with the same name on different boards can be told apart.
function rowMeta(filters: Record<string, any>): string {
  const ent = getSavedViewEntity(filters)
  const summary = filterSummary(filters)
  return ent && ent.name ? `${ent.name} · ${summary}` : summary
}

function close() {
  closeSavedViews()
}

function openView(entry: SavedViewRegistryEntry, preset: SavedFilterRow) {
  if (!vm) return
  const target = buildSavedViewRoute(entry, preset.filters)
  if (!target) return
  closeSavedViews()
  const cur = vm.$route
  const samePage =
    cur.path === target.path &&
    Object.keys(target.query).every((k) => String(cur.query[k] == null ? '' : cur.query[k]) === target.query[k])
  if (samePage) {
    // Same page: change the query so FilterBar's view-param watcher applies it.
    vm.$router.replace({ path: target.path, query: { ...cur.query, view: preset.id } }).catch(() => {})
  } else {
    vm.$router.push({ path: target.path, query: { ...target.query, view: preset.id } }).catch(() => {})
  }
}

function startRename(p: SavedFilterRow) {
  editingId.value = p.id
  editName.value = p.name
  nextTick(() => {
    const el = Array.isArray(editInput.value) ? editInput.value[0] : editInput.value
    el?.focus()
  })
}
function cancelRename() {
  editingId.value = null
  editName.value = ''
}
async function commitRename(p: SavedFilterRow) {
  const name = editName.value.trim()
  if (!name) return cancelRename()
  if (name === p.name) return cancelRename()
  try {
    await renamePreset(p.id, name)
    await load()
  } catch (e: any) {
    swal('Error', e?.response?.status === 409 ? 'A view with that name already exists' : 'Could not rename the view', 'error')
  } finally {
    cancelRename()
  }
}

async function toggleDefault(p: SavedFilterRow) {
  const next: 0 | 1 = p.is_default ? 0 : 1
  // Flip in place instead of reloading. A full load() re-fetches every row and
  // resets which sections are expanded, so the section you just starred would
  // collapse and the list would flicker through its loading state. Update the
  // reactive rows directly (the star binds to is_default) and only one default
  // per view_key, so clear siblings when setting one on.
  const prev = rows.value.map((r) => ({ id: r.id, is_default: r.is_default }))
  for (const r of rows.value) {
    if (r.id === p.id) r.is_default = next
    else if (next === 1 && r.view_key === p.view_key && r.name !== '') r.is_default = 0
  }
  try {
    await setPresetDefault(p.id, next === 1)
  } catch (e) {
    const byId = new Map(prev.map((x) => [x.id, x.is_default]))
    for (const r of rows.value) { const v = byId.get(r.id); if (v != null) r.is_default = v }
    swal('Error', 'Could not update the default view', 'error')
  }
}

async function onDelete(p: SavedFilterRow) {
  const res = await swal({
    title: 'Delete view?',
    text: `"${p.name}" will be removed.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#fd5d93',
    cancelButtonText: 'Cancel',
  })
  if (!res.value) return
  try {
    await deletePreset(p.id)
    await load()
  } catch (e) {
    swal('Error', 'Could not delete the view', 'error')
  }
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && state.isOpen && editingId.value == null) close()
}

watch(
  () => state.isOpen,
  (open) => {
    if (open) {
      document.addEventListener('keydown', onKey)
      load()
    } else {
      document.removeEventListener('keydown', onKey)
      cancelRename()
    }
  },
)

onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
</script>

<script lang="ts">
export default { name: 'SavedViewsDrawer' }
</script>

<style scoped>
.sv-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 4px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}
.sv-empty {
  text-align: center;
  padding: 40px 16px;
}
.sv-empty__icon {
  font-size: 34px;
  color: rgba(225, 78, 202, 0.7);
  margin-bottom: 14px;
}
.sv-empty__title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 8px;
}
.sv-empty__hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  max-width: 320px;
  margin: 0 auto;
  line-height: 1.5;
}

/* Inline SVG icon in a section header (kanban), sized/colored to match the
   tim-icons glyphs used by the other sections. */
.sv-section-icon {
  display: inline-flex;
  align-items: center;
}
.sv-section-icon ::v-deep svg {
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.9);
}

.sv-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;

  &:hover { background: rgba(255, 255, 255, 0.04); }
  & + & { border-top: 1px solid rgba(255, 255, 255, 0.05); }
}
.sv-row__main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sv-row__name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sv-row__meta {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
}
.sv-row__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.sv-row__edit {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sv-input {
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  font-size: 13px;
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  outline: none;

  &:focus { border-color: #e14eca; }
}
.sv-icon-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);

  i { font-size: 14px; }
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
  &--star-on { color: #e14eca; }
  &--confirm:hover { color: #00f2c3; }
  &--danger:hover { color: #fd5d93; }
}
</style>

<style>
/* Light mode */
.white-content .sv-status { color: rgba(0, 0, 0, 0.6); }
.white-content .sv-empty__hint { color: rgba(0, 0, 0, 0.5); }
.white-content .sv-row:hover { background: rgba(0, 0, 0, 0.03); }
.white-content .sv-row + .sv-row { border-top-color: rgba(0, 0, 0, 0.06); }
.white-content .sv-row__meta { color: rgba(0, 0, 0, 0.45); }
.white-content .sv-input {
  color: #1e1e2f;
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.15);
}
.white-content .sv-icon-btn { color: rgba(0, 0, 0, 0.4); }
.white-content .sv-icon-btn:hover { background: rgba(0, 0, 0, 0.06); color: #1e1e2f; }
.white-content .sv-icon-btn--star-on { color: #e14eca; }
.white-content .sv-section-icon svg { color: #9A9A9A; }
</style>
